import { useState, useEffect } from "react";
import { taskGroupsAPI, projectsAPI, tasksAPI } from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  RefreshCw,
  Layers,
  ArrowUpDown,
  ChevronRight,
  ChevronDown,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { format, parseISO } from "date-fns";

const COLORS = [
  { name: "Indigo", value: "#6366f1" },
  { name: "Green", value: "#10b981" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Red", value: "#ef4444" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#8b5cf6" },
  { name: "Pink", value: "#ec4899" },
  { name: "Teal", value: "#14b8a6" },
];

const STATUS_COLORS = {
  "Not Started": "bg-slate-500",
  "In Progress": "bg-blue-500",
  "Completed": "bg-green-500",
  "Overdue": "bg-red-500",
  "Skipping": "bg-amber-500"
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return "-";
  }
}

export default function TaskGroupsPage() {
  const [taskGroups, setTaskGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTaskGroup, setSelectedTaskGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    project_id: "",
    color: "#6366f1"
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, projectsRes, tasksRes] = await Promise.all([
        taskGroupsAPI.getAll(),
        projectsAPI.getAll(),
        tasksAPI.getAll()
      ]);
      setTaskGroups(groupsRes.data.data || []);
      setProjects(projectsRes.data.data || []);
      setAllTasks(tasksRes.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch task groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTaskGroups = [...taskGroups].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aVal = a.fields?.[sortConfig.key] || "";
    const bVal = b.fields?.[sortConfig.key] || "";
    
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredTaskGroups = sortedTaskGroups.filter(group => {
    const name = group.fields?.["Task Group Name"] || group.fields?.Name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getProjectName = (projectIds) => {
    if (!projectIds || projectIds.length === 0) return "-";
    const project = projects.find(p => projectIds.includes(p.id));
    return project?.fields?.["Project Name"] || project?.fields?.Name || "-";
  };

  const getTasksForGroup = (groupId) => {
    return allTasks.filter(task => {
      const taskGroupIds = task.fields?.["Task Group"] || [];
      return taskGroupIds.includes(groupId);
    }).sort((a, b) => {
      const orderA = a.fields?.["Task Order"] || 0;
      const orderB = b.fields?.["Task Order"] || 0;
      return orderA - orderB;
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const openCreateDialog = () => {
    setSelectedTaskGroup(null);
    setFormData({
      name: "",
      project_id: "",
      color: "#6366f1"
    });
    setDialogOpen(true);
  };

  const openEditDialog = (group, e) => {
    e.stopPropagation();
    setSelectedTaskGroup(group);
    setFormData({
      name: group.fields?.["Task Group Name"] || group.fields?.Name || "",
      project_id: group.fields?.["Project Association"]?.[0] || group.fields?.Projects?.[0] || "",
      color: group.fields?.Color || "#6366f1"
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (group, e) => {
    e.stopPropagation();
    setSelectedTaskGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedTaskGroup) {
        await taskGroupsAPI.update(selectedTaskGroup.id, formData);
        toast.success("Task group updated successfully");
      } else {
        await taskGroupsAPI.create(formData);
        toast.success("Task group created successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(selectedTaskGroup ? "Failed to update task group" : "Failed to create task group");
    }
  };

  const handleDelete = async () => {
    try {
      await taskGroupsAPI.delete(selectedTaskGroup.id);
      toast.success("Task group deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task group");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-groups-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Task Groups</h1>
          <p className="text-muted-foreground mt-1">Organize tasks into groups - click to expand</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-task-groups-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={openCreateDialog} data-testid="create-task-group-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Task Group
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search task groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-task-groups-input"
              />
            </div>
            <Badge variant="secondary" className="w-fit">
              {filteredTaskGroups.length} group{filteredTaskGroups.length !== 1 && "s"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredTaskGroups.length > 0 ? (
            <div className="space-y-2">
              {filteredTaskGroups.map((group, index) => {
                const groupTasks = getTasksForGroup(group.id);
                const isExpanded = expandedGroups[group.id];
                const progress = group.fields?.["Progress %"] || 0;
                const progressPct = (progress * 100).toFixed(0);
                
                return (
                  <Collapsible 
                    key={group.id} 
                    open={isExpanded}
                    onOpenChange={() => toggleGroup(group.id)}
                  >
                    <div 
                      className="border rounded-lg overflow-hidden animate-slide-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-3 flex-1">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: group.fields?.Color || "#6366f1" }}
                            />
                            <div className="min-w-0">
                              <div className="font-medium truncate">
                                {group.fields?.["Task Group Name"] || group.fields?.Name || "Untitled"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {getProjectName(group.fields?.["Project Association"] || group.fields?.Projects)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(group.fields?.["Task Group Start Date"])}</span>
                              <span>→</span>
                              <span>{formatDate(group.fields?.["Task Group Due Date"])}</span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary transition-all duration-300"
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium w-10">{progressPct}%</span>
                            </div>
                            
                            <Badge variant="secondary" className="hidden md:inline-flex">
                              {groupTasks.length} task{groupTasks.length !== 1 && "s"}
                            </Badge>
                            
                            <Badge className={STATUS_COLORS[group.fields?.["Progress Status"]] || "bg-slate-500"}>
                              {group.fields?.["Progress Status"] || "Not Started"}
                            </Badge>
                            
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => openEditDialog(group, e)}
                                data-testid={`edit-task-group-${group.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => openDeleteDialog(group, e)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-task-group-${group.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="border-t bg-muted/20">
                          {groupTasks.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/30">
                                  <TableHead className="w-12">#</TableHead>
                                  <TableHead>Task Name</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Start Date</TableHead>
                                  <TableHead>Due Date</TableHead>
                                  <TableHead>Duration</TableHead>
                                  <TableHead>Role</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {groupTasks.map((task) => {
                                  const status = task.fields?.Status || "Not Started";
                                  const isCompleted = status === "Completed";
                                  const isOverdue = status === "Overdue";
                                  
                                  return (
                                    <TableRow 
                                      key={task.id}
                                      className={isCompleted ? "opacity-60" : ""}
                                    >
                                      <TableCell className="text-muted-foreground text-sm">
                                        {task.fields?.["Task Order"] || "-"}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                                          {isOverdue && <AlertCircle className="h-4 w-4 text-red-500" />}
                                          <span className={isCompleted ? "line-through" : ""}>
                                            {task.fields?.["Task Name"] || "Unnamed Task"}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge className={STATUS_COLORS[status] || "bg-slate-500"} variant="secondary">
                                          {status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {formatDate(task.fields?.["Start Date"])}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        <div className="flex items-center gap-1">
                                          {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
                                          {formatDate(task.fields?.["Due Date"])}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {task.fields?.["Task Duration"] || 0} days
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {task.fields?.Role || "-"}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          ) : (
                            <div className="p-6 text-center text-muted-foreground">
                              <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                              <p>No tasks in this group</p>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No task groups found</p>
              <p className="text-sm">Create your first task group to organize tasks</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>
              {selectedTaskGroup ? "Edit Task Group" : "Create New Task Group"}
            </DialogTitle>
            <DialogDescription>
              {selectedTaskGroup 
                ? "Update the task group details below" 
                : "Fill in the details to create a new task group"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter group name"
                  required
                  data-testid="task-group-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project">Associated Project</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                >
                  <SelectTrigger data-testid="task-group-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.fields?.["Project Name"] || project.fields?.Name || "Unnamed"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-md border-2 transition-all duration-200 ${
                        formData.color === color.value 
                          ? "border-foreground scale-110" 
                          : "border-transparent hover:scale-105"
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      title={color.name}
                      data-testid={`color-${color.name.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-task-group-btn">
                {selectedTaskGroup ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTaskGroup?.fields?.["Task Group Name"] || selectedTaskGroup?.fields?.Name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-task-group-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
